// Planes de crédito por nivel (loan-offerings `GET /credit-plans`) para la elegibilidad y la pestaña
// «Cuotas por nivel». Puro: la cuota sale del mismo motor que el simulador de planes.
import type { CreditPlanResponse } from '@/api/services/loan-offerings/loan-offerings.client';
import type { CatalogItem, PlanLevel } from '@/domains/sales/wizard/types';
import { quoteWeekly } from '@/shared/domain/quote';
import { CreditPlanLevelEnum } from '@fiado/type-kit/bin/loanOfferings/enums/CreditPlanLevelEnum';
import { CreditPlanStatusEnum } from '@fiado/type-kit/bin/loanOfferings/enums/CreditPlanStatusEnum';
import { SaleTypeEnum } from '@fiado/type-kit/bin/loanOfferings/enums/SaleTypeEnum';
import { SaleFinancierEnum } from '@fiado/type-kit/bin/retailWizard/enums/SaleFinancierEnum';

/** Orden en que el mock lista los niveles. */
export const PLAN_LEVELS: PlanLevel[] = ['GOLD', 'SILVER', 'BRONZE'];

export interface LevelPlan {
    level: PlanLevel;
    maxFinancedCents: number;
    /** Fracción sobre 1 (0.2 = 20%). */
    minDownPaymentPct: number;
    termWeeks: number;
    /** Fracción anual (2.6 = 260%). */
    tna: number;
    vatRate: number;
    openingFeePct: number;
    /** Gamas que el plan financia; `null` = todas. */
    eligibleTiers: string[] | null;
}

/** Un plan activo de venta nueva por nivel (o el de todos); el plazo es el más largo que ofrece. */
export function toLevelPlans(plans: CreditPlanResponse[]): LevelPlan[] {
    const usable = plans.filter(
        (candidate) =>
            candidate.status === CreditPlanStatusEnum.ACTIVE &&
            candidate.saleTypes.includes(SaleTypeEnum.NEW),
    );
    return PLAN_LEVELS.flatMap((level) => {
        const plan =
            usable.find((candidate) => candidate.level === CreditPlanLevelEnum[level]) ??
            usable.find((candidate) => candidate.level === CreditPlanLevelEnum.ALL);
        if (!plan || plan.termWeeksOptions.length === 0) return [];
        return [
            {
                level,
                maxFinancedCents: plan.maxFinancedAmountCents,
                minDownPaymentPct: plan.minDownPaymentPct,
                termWeeks: Math.max(...plan.termWeeksOptions),
                tna: plan.tnaAnnual,
                vatRate: plan.ivaOnInterest,
                openingFeePct: plan.openingCommissionPct,
                eligibleTiers: plan.eligibleTiers?.map(String) ?? null,
            },
        ];
    });
}

export function acceptsSurekeep(item: CatalogItem): boolean {
    return item.eligibleFinanciers.includes(SaleFinancierEnum.SUREKEEP);
}

export function downPaymentCents(item: CatalogItem, plan: LevelPlan): number {
    return Math.ceil(item.cashPriceCents * plan.minDownPaymentPct);
}

/** Por qué el plan del nivel no alcanza al equipo; `null` si sí lo alcanza. */
export type IneligibleReason = 'NOT_FINANCEABLE' | 'TIER' | 'CAP';

export function ineligibleReason(item: CatalogItem, plan: LevelPlan): IneligibleReason | null {
    if (!acceptsSurekeep(item)) return 'NOT_FINANCEABLE';
    if (plan.eligibleTiers !== null && !plan.eligibleTiers.includes(item.tier)) return 'TIER';
    if (item.cashPriceCents - downPaymentCents(item, plan) > plan.maxFinancedCents) return 'CAP';
    return null;
}

export function isEligible(item: CatalogItem, plan: LevelPlan): boolean {
    return ineligibleReason(item, plan) === null;
}

/** Cuota semanal en centavos con el enganche mínimo; `null` si no es elegible. */
export function weeklyPaymentCents(item: CatalogItem, plan: LevelPlan): number | null {
    if (!isEligible(item, plan)) return null;
    const quote = quoteWeekly({
        price: item.cashPriceCents / 100,
        downPaymentPct: plan.minDownPaymentPct * 100,
        weeks: plan.termWeeks,
        tna: plan.tna,
        vatRate: plan.vatRate,
        openingFeePct: plan.openingFeePct,
    });
    return quote.valid && quote.weeks.length > 0 ? Math.round(quote.weeks[0].payment * 100) : null;
}

/** «$10K» o «$7.5K», la nota de topes del filtro de nivel. */
export function shortThousands(cents: number): string {
    const thousands = cents / 100_000;
    return `$${String(Math.round(thousands * 10) / 10)}K`;
}
